import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Table, Row, Col, InputNumber, Modal, Select, Empty, Tag, Switch, Tabs } from 'antd';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { createPathPage, updatePathPage, deletePathPage, updateCompanyActivePathIds } from '../../../api/companyApi';
import useCompany from '../useCompany';
import { Edit2, Trash2, Plus, ChevronRight, Info, GripVertical } from 'lucide-react';
import { AddRounded, SortOutlined } from "@mui/icons-material";
import NavTrail from '../../../layouts/NavTrail';
import PathPageEditor from './PathPageEditor';
import Article from '../../../components/Article';
import PathQuestionOrder from './PathQuestionOrder';
const { Title, Text } = Typography;

const PathManager = () => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('list');
    const [editingPage, setEditingPage] = useState(null);
    const [isOrderEditorOpen, setIsOrderEditorOpen] = useState(false);
    const { companyDetails, mergedPortfolios, isLoading } = useCompany();
    const pathPages = companyDetails?.path_pages || [];
    const activePathIds = companyDetails?.active_path_ids || [];

    const sortedPathPages = useMemo(() => {
        const activePages = activePathIds
            .map(id => pathPages.find(page => page.id === id))
            .filter(Boolean);
        const inactivePages = pathPages.filter(page => !activePathIds.includes(page.id));
        return [...activePages, ...inactivePages];
    }, [pathPages, activePathIds]);

    const portfolioLookup = useMemo(() =>
        mergedPortfolios.reduce((acc, portfolio) => {
            acc[portfolio.id] = portfolio;
            return acc;
        }, {}),
        [mergedPortfolios]
    );

    const handleCreate = async (values) => {
        await dispatch(createPathPage(values));
        setActiveTab('list');
    };

    const handleUpdate = async (values) => {
        await dispatch(updatePathPage({
            pageId: editingPage.id,
            updateFields: values
        }));
        setEditingPage(null);
        setActiveTab('list');
    };

    const handleDelete = async (pageId) => {
        await dispatch(deletePathPage({ pageId }));
    };

    const handleActiveToggle = (pageId, isActive) => {
        let newActiveIds;
        if (isActive) {
            newActiveIds = [...activePathIds, pageId];
        } else {
            newActiveIds = activePathIds.filter(id => id !== pageId);
        }
        dispatch(updateCompanyActivePathIds({ pathIds: newActiveIds }));
    };

    const columns = [
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, record) => (
                <Switch
                    checked={activePathIds.includes(record.id)}
                    onChange={(checked) => handleActiveToggle(record.id, checked)}
                    className={activePathIds.includes(record.id) ? 'bg-blue-500' : ''}
                />
            )
        },
        {
            title: 'Page Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <div className="flex items-center">
                    <Text strong>{text}</Text>
                    {activePathIds.includes(record.id) && (
                        <Tag className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                            {activePathIds.indexOf(record.id) + 1}
                        </Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Max Picks',
            dataIndex: 'max_select',
            key: 'max_select',
            render: (value) => (
                <Tag bordered={false} className="bg-gradient-to-r from-red-100 to-rose-100 rounded-xl">
                    {value} {value === 1 ? 'Portfolio' : 'Portfolios'}
                </Tag>
            )
        },
        {
            title: '',
            key: 'actions',
            render: (_, record) => (
                <ButtonGroup>
                    <Button
                        variant="text"
                        onClick={() => {
                            setEditingPage(record);
                            setActiveTab('editor');
                        }}
                    >
                        <Edit2 size={16} />
                    </Button>
                    <Button
                        variant="text"
                        danger
                        onClick={() => Modal.confirm({
                            title: 'Delete Portfolio Path Page',
                            content: 'This action cannot be undone. Are you sure you want to proceed?',
                            okText: 'Delete',
                            okButtonProps: { danger: true },
                            onOk: () => handleDelete(record.id)
                        })}
                    >
                        <Trash2 size={16} />
                    </Button>
                </ButtonGroup>
            )
        },
        {
            title: 'Available Portfolios',
            dataIndex: 'portfolio_ids',
            key: 'portfolios',
            render: (portfolioIds = []) => (
                <div className="flex flex-wrap gap-2">
                    {portfolioIds.map(id => (
                        <Tag key={id} bordered={false} className="bg-gradient-to-r from-blue-100 to-sky-100 rounded-xl">
                            {portfolioLookup[id]?.name || 'Unknown'}
                        </Tag>
                    ))}
                </div>
            )
        },
    ];

    const items = [
        {
            key: 'list',
            label: 'Portfolio Pages',
            children: (
                <Table
                    dataSource={sortedPathPages}
                    columns={columns}
                    rowKey="id"
                    loading={isLoading}
                    locale={{
                        emptyText: <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div className="text-center">
                                    <Text strong className="block mb-2">No Portfolio Pages Yet</Text>
                                    <Text type="secondary">Create your first portfolio page to start organizing your investment options</Text>
                                </div>
                            }
                        />
                    }}
                />
            )
        },
        {
            key: 'editor',
            label: editingPage ? `Edit: ${editingPage.title}` : 'Create New Page',
            children: (
                <PathPageEditor
                    page={editingPage}
                    onSave={editingPage ? handleUpdate : handleCreate}
                    onCancel={() => {
                        setActiveTab('list');
                        setEditingPage(null);
                    }}
                />
            )
        }
    ];

    return (
        <div className="min-h-screen">
            <NavTrail items={[{ label: 'Company', path: '/company' }, { label: 'Portfolio Paths' }]} />
            <div className="w-4/5 mx-auto">
                <Card bordered={false}>
                    <div className="w-full mb-8">
                        <Article
                            imgUrl="https://storage.googleapis.com/finiat-web-static-resources/platformIcons/team-with-gear-3d.png"
                            title={<span>Portfolio Grids&trade;</span>}
                            description="Enhance your advisors possibilities by choosing portfolios to include as grid options."
                            subtext="Company"
                            bgColor="bg-gradient-to-b from-sky-50 to-blue-50 border border-blue-100"
                            subtextColor="text-sky-700"
                        />
                    </div>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={items}
                        type="card"
                        className="w-full"
                        tabBarExtraContent={{
                            right: (
                                <ButtonGroup>
                                    <Button
                                        startIcon={<AddRounded />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setEditingPage(null);
                                            setActiveTab('editor');
                                        }}
                                    >
                                        Create New Page
                                    </Button>
                                    <Button
                                        startIcon={<SortOutlined />}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setIsOrderEditorOpen(true)}
                                        disabled={activePathIds.length < 2}
                                    >
                                        Edit Order
                                    </Button>
                                </ButtonGroup>
                            )
                        }}
                    />
                </Card>
            </div>
            <PathQuestionOrder
                open={isOrderEditorOpen}
                onClose={() => setIsOrderEditorOpen(false)}
                pathPages={pathPages}
                activePathIds={activePathIds}
                onOrderChange={(newOrder) => {
                    dispatch(updateCompanyActivePathIds({ pathIds: newOrder }));
                }}
            />
        </div>
    );
};

export default PathManager;